import { ApolloClient, gql, InMemoryCache, NormalizedCacheObject, createHttpLink } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import GetEnvironment from "../enviroments"

export class QuadriApi {
  private baseUrl: string
  private client: ApolloClient<NormalizedCacheObject>

  constructor(baseUrl?: string) {
    console.log("Getting environment for API")
    const QuadriApiBaseUrl = GetEnvironment().quadriGraphQlUrl
    console.log("Using graphql url: ", QuadriApiBaseUrl)

    this.baseUrl = baseUrl || QuadriApiBaseUrl

    // See https://www.apollographql.com/docs/react/networking/authentication/
    const link = authLink.concat(createHttpLink({ uri: this.baseUrl }))
    this.client = new ApolloClient({
      cache: new InMemoryCache(),
      link
    })
  }

  public getClient(): ApolloClient<NormalizedCacheObject> {
    return this.client
  }
}

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const accessToken = localStorage.getItem("tc-access-token")
  const idToken = localStorage.getItem("tc-id-token")
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : "",
      "x-jwt-assertion": idToken ? `Bearer ${idToken}` : ""
    }
  }
})

export const GET_ACTIVITIES = gql`
  query GET_ACTIVITIES($quadriProjectId: String!, $filter: ActivitiesFilter!, $offset: Int) {
    getActivities(quadriProjectId: $quadriProjectId, filter: $filter, offset: $offset) {
      type
      time
      userId
      description
      tasksAffectedCount
      worksetName
      taskChanges {
        taskId
        changeType
        taskName
        taskType
      }
      secondsSpent
      activityId
      isImplicit
    }
  }
`

export const GET_WORKSETS = gql`
  query GET_WORKSETS($quadriProjectId: String!, $filter: WorksetsFilter!) {
    getWorksets(quadriProjectId: $quadriProjectId, filter: $filter) {
      id
      name
      status
      userId
      createdTime
      reservedTaskIds
      reservedFeatures {
        featureTypeName
      }
      activity {
        type
        description
        time
      }
    }
  }
`

export const GET_WORKSET_TASKS = gql`
  query GET_WORKSET_TASKS($quadriProjectId: String!, $taskIds: [String!]!, $include_deleted: Boolean!) {
    getTasks(quadriProjectId: $quadriProjectId, taskIds: $taskIds, include_deleted: $include_deleted) {
      id
      name
      type
      taskPath {
        name
      }
    }
  }
`

export const GET_PROJECT_STATUS = gql`
  query GET_PROJECT_STATUS($id: String!) {
    tryGetConnectQuadriProject(connectProjectId: $id) {
      found
      project {
        quadriProjectId
        projectStatus
        featureCatalogId
      }
      error
    }
  }
`

export const GET_CONFIG = gql`
  query GET_CONFIG($id: String!) {
    tryGetConnectQuadriProject(connectProjectId: $id) {
      found
      project {
        quadriProjectId
        connectProjectId
        featureCatalogId
        coordinateSystemUnit
        coordinateSystemName
        projectStatusMessage
        projectStatus
        projectUpdateMessage
        createdAt
        updatedAt
        deletedAt
        permissionsUpdatedAt
        projectCreatedWithStatus
        customerAccountId
        customerAccountName
        projectArea {
          latitude
          longitude
        }
      }
      error
    }
  }
`

export const GET_ROOTTASKS = gql`
  query GET_ROOTTASKS($quadriProjectId: String!, $include_deleted: Boolean!) {
    getProjectRootTasks(quadriProjectId: $quadriProjectId, include_deleted: $include_deleted) {
      id
      name
      guid
      type
      deleted
      lastModifiedBy
      lastModifiedTime
      createdTime
      subTaskIds
    }
  }
`

export const GET_TASKS = gql`
  query GET_TASKS($quadriProjectId: String!, $taskIds: [String!]!, $include_deleted: Boolean!) {
    getTasks(quadriProjectId: $quadriProjectId, taskIds: $taskIds, include_deleted: $include_deleted) {
      id
      name
      guid
      lastModifiedBy
      lastModifiedTime
      createdTime
      type
      deleted
      subTaskIds
      taskPath {
        id
        name
      }
    }
  }
`

export const GET_SUBTASKS = gql`
  query GET_TASKS($quadriProjectId: String!, $taskIds: [String!]!, $include_deleted: Boolean!) {
    getTasks(quadriProjectId: $quadriProjectId, taskIds: $taskIds, include_deleted: $include_deleted) {
      id
      name
      guid
      lastModifiedBy
      lastModifiedTime
      createdTime
      type
      deleted
      subTaskIds
      subTasks {
        id
        name
        guid
        type
        deleted
        lastModifiedBy
        lastModifiedTime
        createdTime
        subTaskIds
      }
      taskPath {
        id
        name
      }
    }
  }
`

export const GET_NOTIFICATIONS = gql`
  query GetNotifications {
    getNotifications {
      title
      description
      pubDate
      link
      guid
    }
  }
`

export const HAS_ACCESS = gql`
  query HAS_ACCESS {
    hasAccess {
      access
    }
  }
`
export const HAS_PROJECT_ACCESS = gql`
  query HAS_PROJECT_ACCESS($connectProjectId: String!) {
    hasProjectAccess(connectProjectId: $connectProjectId) {
      projectAccess
    }
  }
`

export const CUSTOMER_IS_ELIGIBLE = gql`
  query CUSTOMER_IS_ELIGIBLE($connectAccountId: String!) {
    customerEligibleForQuadri(connectAccountId: $connectAccountId) {
      eligible
      message
    }
  }
`

export const ELIGIBLE_ACCOUNTS = gql`
  query ELIGIBLE_ACCOUNTS {
    eligibleAccountsWithLicenses {
      companyIdEms
      companyName
    }
  }
`

export const FEATURE_CATALOG_STANDARDS = gql`
  query FEATURE_CATALOG_STANDARDS($tags: String) {
    featureCatalogStandards(tags: $tags) {
      id
      name
    }
  }
`

export const FEATURE_CATALOGS = gql`
  query FEATURE_CATALOGS($standardId: String, $minVersion: String, $tags: String) {
    featureCatalogs(standardId: $standardId, minVersion: $minVersion, tags: $tags) {
      id
      name
      standard
      version
      date
      displayText
    }
  }
`

export const COMPATIBLE_FEATURE_CATALOGS = gql`
  query COMPATIBLE_FEATURE_CATALOGS($id: String!, $tags: String) {
    compatibleFeatureCatalogs(featureCatalogId: $id, tags: $tags) {
      id
      name
      standard
      version
      date
      displayText
    }
  }
`

export const PUBLIC_FEATURE_CATALOG_INFO = gql`
  query PUBLIC_FEATURE_CATALOG_INFO($id: String!) {
    publicFeatureCatalogInfo(featureCatalogId: $id) {
      id
      name
      standard
      version
      date
      displayText
    }
  }
`

export const CREATE_PROJECT = gql`
  mutation CREATE_PROJECT($project: CreateProjectRequest!) {
    createProject(request: $project) {
      projectStatus
      projectId
    }
  }
`

export const SYNC_PROJECT = gql`
  mutation SYNC_PROJECT($project: SyncProjectRequest!) {
    syncProject(request: $project) {
      projectStatus
    }
  }
`

export const DELETE_PROJECT = gql`
  mutation DELETE_PROJECT($deleteProjectRequest: DeleteProjectRequest!) {
    deleteProject(request: $deleteProjectRequest) {
      success
    }
  }
`

export const DISCONNECT_WORKSET = gql`
  mutation DISCONNECT_WORKSET($quadriProjectId: String!, $worksetId: String!) {
    disconnectWorkset(quadriProjectId: $quadriProjectId, worksetId: $worksetId) {
      success
    }
  }
`

export const GET_PROJECT_ACCESS = gql`
  query GET_PROJECT_ACCESS($quadriProjectId: String!, $connectProjectId: String!) {
    getProjectAccess(quadriProjectId: $quadriProjectId, connectProjectId: $connectProjectId) {
      defaultAccess
      userOrGroupAccess {
        access
        id
        email
        type
      }
    }
  }
`

export const UPDATE_PROJECT_ACCESS = gql`
  mutation UpdateProjectAccess($quadriProjectId: String!, $request: UpdateProjectAccessRequest!) {
    updateProjectAccess(quadriProjectId: $quadriProjectId, request: $request) {
      success
    }
  }
`

export const GET_PROJECT_SIZE = gql`
  query GET_PROJECT_SIZE($quadriProjectId: String!) {
    getProjectSize(quadriProjectId: $quadriProjectId) {
      project_size_pretty
    }
  }
`

export const GET_PROJECTEDCOORDINATES = gql`
  query getProjectedCoordinates($csib64: String!, $boundaryCoordinates: String!) {
    getProjectedCoordinates(csib64: $csib64, boundaryCoordinates: $boundaryCoordinates) {
      coordinates
    }
  }
`

export const GET_GEOCOORDINATES = gql`
  query getGeoCoordinates($csib64: String!, $projectedCoordinates: String!) {
    getGeoCoordinates(csib64: $csib64, projectedCoordinates: $projectedCoordinates) {
      coordinates
    }
  }
`
